// Timeline feature guide — locale-keyed in-code help content.
// `content.<lang>` carries the localized { feature, title, sections }.
// Each section: { id, title, videoUrl (optional), text (markdown-lite) }.
// `videoUrl` null → the popup shows a "coming soon" placeholder.

const timelineGuide = {
  id: "timeline",
  icon: "movie",
  // Search keywords span all languages so a query hits regardless of UI language.
  keywords: [
    "timeline",
    "zaman çizelgesi",
    "animation",
    "animasyon",
    "effect",
    "efekt",
    "duration",
    "süre",
    "scene length",
    "sahne süresi",
    "play",
    "oynat",
    "preview speed",
    "önizleme hızı",
    "scrub",
    "playhead",
    "keyframe",
    "entrance",
    "giriş",
    "exit",
    "çıkış",
    "pan",
    "ken burns",
    "zoom",
    "yakınlaştır",
    "ghost",
    "narration",
    "anlatım",
    "sync",
    "split",
    "böl",
    "cut and delete",
    "kes ve sil",
    "trim",
    "kırp",
    "loop",
    "döngü",
    "align",
    "hizala",
    "row",
    "satır",
    "media assets",
    "medya varlıkları",
    "línea de tiempo",
    "animación",
    "efecto",
    "duración",
    "reproducir",
    "cabezal",
    "entrada",
    "salida",
    "acercar",
    "narración",
    "sincronizar",
    "dividir",
    "cortar y eliminar",
    "bucle",
    "alinear",
    "fila",
    "recursos multimedia",
    "quick update",
    "update now",
    "old training",
    "legacy",
    "missing sound",
    "kısa bir güncelleme",
    "şimdi güncelle",
    "eski eğitim",
    "ses kayboldu",
    "actualización rápida",
    "actualizar ahora",
    "formación antigua",
  ],
  content: {
    en: {
      feature: "Timeline",
      title: "Timeline & animation",
      sections: [
        {
          id: "overview",
          title: "What the timeline is",
          videoUrl: null,
          text:
            "The timeline is docked under the canvas and controls **when** each element appears, how long it stays and how it moves. Collapse or expand it with the Timeline button at its top-left, and drag its top edge to make it taller or shorter.\n\n" +
            "- Each **row** holds clips. Elements of the same kind (images, videos, texts, audio and narration) share one row and play one after another; shapes, interactive and smart elements get a row of their own.\n" +
            "- **Left → right is time.** A clip's bar shows when that element is on screen — its start and end.\n" +
            "- The vertical line is the **playhead**: the moment currently shown on the canvas.\n" +
            "- Rows follow the stacking order: the top row is the front-most element on the canvas.\n\n" +
            "Click a clip to select its element on the canvas. If the selected element isn't visible at the playhead, the playhead jumps to where it is.\n\n" +
            "If the timeline is missing, check that **Timeline Visible** is switched on in **Global Configuration**.",
        },
        {
          id: "play-scrub",
          title: "Play & scrub",
          videoUrl: null,
          text:
            "Preview the scene without leaving the editor.\n\n" +
            "- **Play / Pause** runs the scene from the playhead. Playback stops at the end of the scene; pressing Play again starts over from the beginning.\n" +
            "- **Seek to start** (the skip-back button) jumps the playhead back to 00:00.\n" +
            "- **Preview speed** (next to Play): the gauge button steps to the next speed, and the arrow opens a list from 0.5× to 4×. It only affects the editor preview, not the published training; YouTube videos allow at most 2×.\n" +
            "- Drag the playhead, or click and drag on the ruler, to **scrub** to any moment. The readout shows the current time over the scene length, in minutes:seconds.hundredths.\n" +
            "- When playback reaches an interactive element it pauses and shows an **Interactive** badge; press **Skip** to continue without completing the interaction.\n\n" +
            "To see elements that are outside their time window while you work, use ghost mode (see Zoom, ghost mode, hide-all, lock-all).",
        },
        {
          id: "box-timing",
          title: "Box timing & duration",
          videoUrl: null,
          text:
            "Control when an element shows up and how long it stays.\n\n" +
            "- Drag the **left edge** of a clip to change when the element **appears**; drag the **right edge** to change when it **leaves**. On video and audio clips the edges trim the media rather than stretching it.\n" +
            "- Drag the middle of a clip to move it without changing its length. Edges snap to the playhead, the scene start and end, and other clips' edges; a guide line shows the snap.\n" +
            "- On a selected video that plays automatically, the inner play bar sets which part of the video plays and when it starts; drag the bar or its edges.\n" +
            "- New elements are placed at the playhead, not at the start of the scene, and join a free row of their kind.\n\n" +
            "An element with no end time stays on screen until the scene ends. The scene's own length is set separately — see Scene length.",
        },
        {
          id: "animations",
          title: "Entrance & exit animations",
          videoUrl: null,
          text:
            "Give an element motion as it comes in or leaves.\n\n" +
            "- Select the element and open **Effect** in the right menu (or right-click a non-media clip and pick the animation icon). The panel has **Entrance** and **Exit** tabs.\n" +
            "- Entrance presets: **Pan In**, **Fade In**, **Scale In**, **Slide In Right / Left / Up / Down**, **Rotate**, **Bounce**, **Bounce In**, **Rotate In**, **Flip**, **Pinwheel**, **Roll** and **Stretch**. Text elements also get **Typewriter** and **Wavy Text**.\n" +
            "- Exit presets: **Pan Out**, **Fade Out**, **Scale Out**, **Slide Out Right / Left / Up / Down**, **Rotate Out** and **Bounce Out** (plus **Typewriter Out** and **Wavy Text Out** for text).\n" +
            "- The first, crossed-out card removes the effect. The slider sets how long the effect runs (up to 3 seconds; for Pan, up to the clip's length).\n" +
            "- **Pan (Ken Burns)** is a slow zoom toward or away from a point. Set **Direction** (**Zoom In** / **Zoom Out**), **Zoom Amount**, and the **Start Point** on the picker or as X/Y percentages. It gives screenshot and video scenes that “show closer” feel.\n\n" +
            "The entrance runs at the clip's start and the exit at its end. On the timeline a shaded band marks each one on the clip, and the **Effect** button shows a small dot when the element has either.",
        },
        {
          id: "narration",
          title: "Narration & Sync with AI",
          videoUrl: null,
          text:
            "Line the visuals up with the voice-over.\n\n" +
            "- Scene narration and sounds appear as audio clips with a waveform, on the same time ruler as the elements. Narration rows sit below the element rows.\n" +
            "- Double-click a narration clip to open it in the sound dialog.\n" +
            "- Right-click a narration clip and choose **Repeat for scene** to loop it for the whole scene.\n" +
            "- **Sync with AI** in the timeline toolbar asks for confirmation (**Start Synchronization**), then times the scene's images and texts to its audio. You can still adjust any clip by hand afterwards. The Sync with AI guide covers it in detail.",
        },
        {
          id: "zoom-bulk",
          title: "Zoom, ghost mode, hide-all, lock-all",
          videoUrl: null,
          text:
            "Work faster on busy scenes. These controls sit in the top-left corner of the timeline, above the row labels.\n\n" +
            "- **Zoom out** (−) and **Zoom in** (+) change the time scale; click the percentage to **Reset zoom (100%)**. Until you zoom or click in the tracks, the timeline fits the whole scene to its width.\n" +
            "- The dashed-square button turns ghost mode on or off. With it on and playback stopped, elements outside their time window are drawn faded in place instead of hidden, so you can position things that aren't on screen yet.\n" +
            "- **Hide all** / **Show all** and **Lock all** / **Unlock all** toggle every row at once.\n" +
            "- Each row label also has its own eye (**Hide** / **Show**) and lock (**Lock** / **Unlock**). Hidden elements are hidden for learners too; locked elements can't be moved or resized on the canvas or on the timeline.",
        },
        {
          id: "shared-rows",
          title: "Shared rows & stacking order",
          videoUrl: null,
          text:
            "Elements of the same kind share a row so the timeline stays compact.\n\n" +
            "- A shared row shows a type icon with a member count; its eye and lock act on every clip in the row.\n" +
            "- Dropping a clip onto an occupied spot pushes the neighbouring clips along instead of overlapping them. Only rows of the same kind accept the clip; drop it between two rows to give it a row of its own.\n" +
            "- Drag the grip on a row label up or down to change the stacking order — a higher row is in front on the canvas. Locked rows can't be moved.\n" +
            "- A clip added while you're scrolled elsewhere is scrolled into view and pulses briefly.\n" +
            "- Smart elements with animated parts show an arrow on their clip; expand it to get one sub-row per part, each with its own timing.",
        },
        {
          id: "clip-actions",
          title: "Clip right-click menu",
          videoUrl: null,
          text:
            "Right-click a clip for quick actions. Hover an icon to see its name; the two cut actions also explain what they do, or why they're unavailable.\n\n" +
            "- **Split at playhead** cuts a video or audio clip in two at the playhead. The pieces carry a link badge (**Split clip — same source**), and hovering one outlines its siblings.\n" +
            "- **Cut & delete** removes the part of the clip before the playhead and pulls the rest back to the clip's start; hovering it previews the slice that will be removed.\n" +
            "- Both cuts need the playhead inside the clip; otherwise the menu says **Move the playhead inside the clip first**.\n" +
            "- **Loop** repeats a video clip; **Repeat for scene** loops a narration for the whole scene.\n" +
            "- **Align to previous element** / **Align to next element** move the clip up against its neighbour.\n" +
            "- **Remove** deletes the element. With a clip selected, the Delete key does the same.\n" +
            "- On image clips, **Aidea Wizard AI** opens the AI image tools.",
        },
        {
          id: "scene-duration",
          title: "Scene length",
          videoUrl: null,
          text:
            "The scene's length is set separately from its clips.\n\n" +
            "- The readout in the toolbar shows the current time over the scene length. Click the total to type a new length (minutes:seconds) and press Enter.\n" +
            "- Or drag the scene-end marker on the tracks; it can't go below 5 seconds.\n" +
            "- The lock next to the total (and on the marker) freezes the scene length so it can't be changed by accident. Click it again to unlock.\n\n" +
            "Changing the scene length never changes how long elements stay. A new scene takes its length from its content, and **Sync with AI** refits it to the narration.",
        },
        {
          id: "media-assets",
          title: "Media Assets view",
          videoUrl: null,
          text:
            "The grid button in the timeline toolbar (**Show Media Assets**) opens **Media Assets**, an overview of the videos, images and audio used in the training.\n\n" +
            "- Filter by type from the category list on the left.\n" +
            "- **Current Scene Only** is on by default; turn it off to see every scene's media.\n" +
            "- Click an item to preview it, or play an audio item in place. With all scenes shown, each item's menu offers **Preview** and **Go to Scene**.",
        },
        {
          id: "legacy-update",
          title: "Updating older trainings",
          videoUrl: null,
          text:
            "Trainings made before the new timeline can have videos and audio clips with no saved length on the timeline. When you open such a training, a **Quick update needed** dialog appears. Without the update, scenes can run short and exported videos can lose their sound.\n\n" +
            "- The dialog shows how many videos and audio clips need the update and how many scenes are affected.\n" +
            "- **Update now** checks each media file's real length (**Checking media files…** with a counter), writes it to the timeline and then saves the training. It takes a few seconds and nothing else in the training changes. The whole update is a single undo step.\n" +
            "- **Not now** closes the dialog; it appears again the next time you open the training.\n" +
            "- If the update can't finish, check your connection and click **Try again**.",
        },
      ],
    },
    tr: {
      feature: "Zaman Çizelgesi",
      title: "Zaman çizelgesi ve animasyon",
      sections: [
        {
          id: "overview",
          title: "Zaman çizelgesi nedir",
          videoUrl: null,
          text:
            "Zaman çizelgesi tuvalin altında yer alır; her öğenin **ne zaman** görüneceğini, ne kadar kalacağını ve nasıl hareket edeceğini kontrol eder. Sol üstteki Timeline (Zaman Çizelgesi) düğmesiyle açıp kapatın; üst kenarını sürükleyerek yüksekliğini değiştirin.\n\n" +
            "- Her **satır** klipler içerir. Aynı türdeki öğeler (görseller, videolar, metinler, sesler ve anlatım) tek bir satırı paylaşır ve art arda oynar; şekiller, etkileşimli ve akıllı öğeler kendi satırlarını alır.\n" +
            "- **Soldan sağa zamandır.** Klibin çubuğu, o öğenin ekranda olduğu aralığı — başlangıç ve bitişini — gösterir.\n" +
            "- Dikey çizgi **oynatma başlığıdır (playhead)**: tuvalde o anda gösterilen an.\n" +
            "- Satırlar katman sırasını izler: en üstteki satır, tuvalde en öndeki öğedir.\n\n" +
            "Bir klibe tıklayarak öğesini tuvalde seçin. Seçili öğe oynatma başlığının bulunduğu anda görünmüyorsa oynatma başlığı öğenin göründüğü ana atlar.\n\n" +
            "Zaman çizelgesi görünmüyorsa **Genel Konfigürasyon** içindeki Zaman Çizelgesi anahtarının açık olduğunu kontrol edin.",
        },
        {
          id: "play-scrub",
          title: "Oynat ve gezin",
          videoUrl: null,
          text:
            "Sahneyi editörden çıkmadan önizleyin.\n\n" +
            "- **Oynat / Durdur** sahneyi oynatma başlığından itibaren çalıştırır. Oynatma sahnenin sonunda durur; Oynat'a yeniden basınca sahne baştan başlar.\n" +
            "- **Başa al** (geri atlama düğmesi) oynatma başlığını 00:00'a döndürür.\n" +
            "- **Önizleme hızı** (Oynat'ın yanında): gösterge düğmesi bir sonraki hıza geçer, ok ise 0.5× ile 4× arasındaki listeyi açar. Yalnızca editördeki önizlemeyi etkiler, yayınlanan eğitimi etkilemez; YouTube videoları en fazla 2× destekler.\n" +
            "- Oynatma başlığını sürükleyerek ya da cetvele tıklayıp sürükleyerek istediğiniz ana **gidin**. Süre göstergesi mevcut anı ve sahne uzunluğunu dakika:saniye.salise biçiminde gösterir.\n" +
            "- Oynatma etkileşimli bir öğeye geldiğinde durur ve **Etkileşim** rozeti gösterir; etkileşimi tamamlamadan devam etmek için **Atla**'ya basın.\n\n" +
            "Zaman aralığı dışındaki öğeleri çalışırken görmek için hayalet modunu kullanın (bkz. Yakınlaştırma, hayalet mod, tümünü gizle/kilitle).",
        },
        {
          id: "box-timing",
          title: "Kutu zamanlaması ve süre",
          videoUrl: null,
          text:
            "Bir öğenin ne zaman görüneceğini ve ne kadar kalacağını ayarlayın.\n\n" +
            "- Klibin **sol kenarını** sürükleyerek öğenin **görünme** anını, **sağ kenarını** sürükleyerek **kaybolma** anını değiştirin. Video ve ses kliplerinde kenarlar medyayı esnetmez, kırpar.\n" +
            "- Klibi ortasından sürükleyerek uzunluğunu değiştirmeden taşıyın. Kenarlar oynatma başlığına, sahnenin başına ve sonuna ve diğer kliplerin kenarlarına yapışır; yapışmayı bir kılavuz çizgi gösterir.\n" +
            "- Otomatik oynayan bir video seçiliyken klibin içindeki oynatma çubuğu, videonun hangi bölümünün ve ne zaman oynayacağını belirler; çubuğu veya kenarlarını sürükleyin.\n" +
            "- Yeni öğeler sahnenin başına değil, oynatma başlığının bulunduğu ana yerleştirilir ve kendi türündeki boş bir satıra katılır.\n\n" +
            "Bitiş zamanı olmayan bir öğe sahne bitene kadar ekranda kalır. Sahnenin kendi uzunluğu ayrıca ayarlanır — bkz. Sahne uzunluğu.",
        },
        {
          id: "animations",
          title: "Giriş ve çıkış animasyonları",
          videoUrl: null,
          text:
            "Bir öğeye girerken veya çıkarken hareket kazandırın.\n\n" +
            "- Öğeyi seçip sağ menüde **Efekt**'i açın (veya medya olmayan bir klibe sağ tıklayıp animasyon simgesini seçin). Panelde **Giriş** ve **Çıkış** sekmeleri vardır.\n" +
            "- Giriş efektleri: **Pan Giriş**, **Aydınlatma**, **Yakınlaştır**, **Sağa Kaydır**, **Soldan Gir**, **Yukarı Kaydır**, **Aşağı Kaydır**, **Döndür**, **Zıpla**, **Zıplayarak Gir**, **Dönerek Gir**, **Çevir**, **Fırıldak**, **Yuvarlan** ve **Esne**. Metin öğelerinde ayrıca **Daktilo** ve **Dalgalı Metin** bulunur.\n" +
            "- Çıkış efektleri: **Pan Çıkış**, **Karart**, **Uzaklaştır**, **Sağa Çık**, **Sola Çık**, **Yukarı Çık**, **Aşağı Çık**, **Dönerek Çık** ve **Zıplayarak Çık** (metinde ayrıca **Daktilo (Çıkış)** ve **Dalgalı Metin (Çıkış)**).\n" +
            "- İlk, üstü çizili kart efekti kaldırır. Kaydırıcı efektin süresini ayarlar (en fazla 3 saniye; Pan'da klibin uzunluğu kadar).\n" +
            "- **Pan (Ken Burns)** bir noktaya doğru yavaşça yakınlaşan ya da uzaklaşan bir zoom'dur. **Yön** (**Yakınlaştır** / **Uzaklaştır**), **Zoom Miktarı** ve **Başlangıç Noktası**'nı seçiciden veya X/Y yüzdesi olarak ayarlayın. Ekran görüntüsü ve video sahnelerine o “daha yakından gösterme” hissini verir.\n\n" +
            "Giriş klibin başında, çıkış ise sonunda çalışır. Zaman çizelgesinde klip üzerindeki gölgeli bir bant her birini işaretler; öğede giriş ya da çıkış efekti varsa **Efekt** düğmesinde küçük bir nokta görünür.",
        },
        {
          id: "narration",
          title: "Anlatım ve AI ile Senkronize Et",
          videoUrl: null,
          text:
            "Görselleri seslendirmeyle hizalayın.\n\n" +
            "- Sahne anlatımı ve sesleri, öğelerle aynı zaman cetveli üzerinde dalga formlu ses klipleri olarak görünür. Anlatım satırları öğe satırlarının altında yer alır.\n" +
            "- Bir anlatım klibine çift tıklayarak onu ses penceresinde açın.\n" +
            "- Bir anlatım klibine sağ tıklayıp **Sahne Boyunca Tekrarla**'yı seçerek tüm sahne boyunca döngüye alın.\n" +
            "- Zaman çizelgesi araç çubuğundaki **AI ile Senkronize Et** önce onay ister (**Senkronizasyonu Başlat**), ardından sahnedeki görsel ve metinlerin zamanlamasını sese göre ayarlar. Sonrasında istediğiniz klibi elle yine düzenleyebilirsiniz. Ayrıntılar AI ile Senkronize Et rehberindedir.",
        },
        {
          id: "zoom-bulk",
          title: "Yakınlaştırma, hayalet mod, tümünü gizle/kilitle",
          videoUrl: null,
          text:
            "Yoğun sahnelerde daha hızlı çalışın. Bu kontroller zaman çizelgesinin sol üst köşesinde, satır etiketlerinin üzerindedir.\n\n" +
            "- **Uzaklaştır** (−) ve **Yakınlaştır** (+) zaman ölçeğini değiştirir; **Zoom'u sıfırla (%100)** için yüzdeye tıklayın. Siz yakınlaştırana ya da kliplerin olduğu alana tıklayana kadar zaman çizelgesi tüm sahneyi genişliğine sığdırır.\n" +
            "- Kesik çizgili kare düğmesi hayalet modu açıp kapatır. Açıkken ve oynatma durmuşken, zaman aralığı dışındaki öğeler gizlenmek yerine yerlerinde soluk çizilir; böylece henüz ekranda olmayan öğeleri konumlandırabilirsiniz. Düğmenin ipucu **Tüm elementleri göster (zaman dışındakiler soluk)** der.\n" +
            "- **Tümünü Gizle** / **Tümünü Göster** ve **Tümünü Kilitle** / **Tümünü Aç** her satırı tek seferde değiştirir.\n" +
            "- Her satır etiketinin kendi göz (**Gizle** / **Göster**) ve kilit (**Kilitle** / **Kilidi Aç**) düğmesi vardır. Gizlenen öğeler öğrenciler için de gizlenir; kilitli öğeler tuvalde veya zaman çizelgesinde taşınamaz ve yeniden boyutlandırılamaz.",
        },
        {
          id: "shared-rows",
          title: "Ortak satırlar ve katman sırası",
          videoUrl: null,
          text:
            "Zaman çizelgesi derli toplu kalsın diye aynı türdeki öğeler bir satırı paylaşır.\n\n" +
            "- Ortak bir satır, tür simgesini ve içindeki klip sayısını gösterir; gözü ve kilidi satırdaki tüm kliplere uygulanır.\n" +
            "- Bir klibi dolu bir yere bıraktığınızda komşu klipler üst üste binmek yerine ileri itilir. Klibi yalnızca aynı türdeki satırlar kabul eder; kendi satırını almasını istiyorsanız iki satırın arasına bırakın.\n" +
            "- Katman sırasını değiştirmek için satır etiketindeki tutamacı yukarı veya aşağı sürükleyin — üstteki satır tuvalde öndedir. Kilitli satırlar taşınamaz.\n" +
            "- Siz başka bir yere kaydırmışken eklenen bir klip görünür alana kaydırılır ve kısa bir süre yanıp söner.\n" +
            "- Hareketli parçaları olan akıllı öğelerin klibinde bir ok bulunur; açtığınızda her parça için kendi zamanlaması olan bir alt satır görünür.",
        },
        {
          id: "clip-actions",
          title: "Klip sağ tık menüsü",
          videoUrl: null,
          text:
            "Hızlı işlemler için bir klibe sağ tıklayın. Bir simgenin üzerine gelince adı görünür; iki kesme işlemi ayrıca ne yaptığını ya da neden kullanılamadığını açıklar.\n\n" +
            "- **Playhead'den Böl** bir video veya ses klibini oynatma başlığından ikiye böler. Parçalarda bir bağlantı rozeti bulunur (**Bölünmüş parça — kaynağı aynı**) ve birinin üzerine gelince diğer parçalar vurgulanır.\n" +
            "- **Kes ve Sil** klibin oynatma başlığından önceki kısmını siler ve kalanını klibin başlangıcına çeker; üzerine gelince silinecek kısım önizlenir.\n" +
            "- İki kesme işlemi de oynatma başlığının klibin içinde olmasını gerektirir; değilse menüde **Önce playhead'i klibin içine getirin** yazar.\n" +
            "- **Döngü** bir video klibini tekrarlatır; **Sahne Boyunca Tekrarla** bir anlatımı tüm sahne boyunca döngüye alır.\n" +
            "- **Önceki elemente hizala** / **Sonraki elemente hizala** klibi komşusuna dayar.\n" +
            "- **Kaldır** öğeyi siler. Bir klip seçiliyken Delete tuşu da aynı işi yapar.\n" +
            "- Görsel kliplerinde **Aidea Wizard AI** (Yapay Zekâ Sihirbazı) yapay zekâ görsel araçlarını açar.",
        },
        {
          id: "scene-duration",
          title: "Sahne uzunluğu",
          videoUrl: null,
          text:
            "Sahnenin uzunluğu kliplerinden bağımsız olarak ayarlanır.\n\n" +
            "- Araç çubuğundaki süre göstergesi mevcut anı ve sahne uzunluğunu gösterir. Yeni bir uzunluk yazmak için toplam süreye tıklayın (dakika:saniye) ve Enter'a basın.\n" +
            "- Ya da kliplerin olduğu alandaki sahne sonu işaretini sürükleyin; 5 saniyenin altına inemez.\n" +
            "- Toplam sürenin yanındaki (ve işaretin üzerindeki) kilit, sahne uzunluğunun yanlışlıkla değişmemesi için onu sabitler. Kilidi açmak için yeniden tıklayın.\n\n" +
            "Sahne uzunluğunu değiştirmek öğelerin ne kadar kalacağını değiştirmez. Yeni bir sahne uzunluğunu içeriğinden alır; **AI ile Senkronize Et** ise uzunluğu anlatıma göre yeniden ayarlar.",
        },
        {
          id: "media-assets",
          title: "Medya Varlıkları görünümü",
          videoUrl: null,
          text:
            "Zaman çizelgesi araç çubuğundaki ızgara düğmesi (**Medya Varlıklarını Göster**), eğitimde kullanılan video, görsel ve seslere genel bakış sunan **Medya Varlıkları** penceresini açar.\n\n" +
            "- Soldaki kategori listesinden türe göre filtreleyin.\n" +
            "- **Sadece bu sahne** varsayılan olarak açıktır; tüm sahnelerin medyasını görmek için kapatın.\n" +
            "- Önizlemek için bir öğeye tıklayın; ses öğelerini yerinde çalabilirsiniz. Tüm sahneler gösterilirken her öğenin menüsünde **Önizle** ve **Sahneye Git** bulunur.",
        },
        {
          id: "legacy-update",
          title: "Eski eğitimleri güncelleme",
          videoUrl: null,
          text:
            "Yeni zaman çizelgesinden önce hazırlanan eğitimlerde, zaman çizelgesinde kayıtlı bir süresi olmayan video ve ses öğeleri bulunabilir. Böyle bir eğitimi açtığınızda **Kısa bir güncelleme gerekiyor** penceresi görünür. Güncelleme yapılmazsa sahneler kısa kalabilir ve dışa aktarılan videolarda ses kaybolabilir.\n\n" +
            "- Pencere, güncellenmesi gereken video ve ses öğelerinin sayısını ve kaç sahnenin etkilendiğini gösterir.\n" +
            "- **Şimdi güncelle** her medya dosyasının gerçek süresini kontrol eder (sayaçla birlikte **Medya dosyaları kontrol ediliyor…**), bu süreyi zaman çizelgesine yazar ve ardından eğitimi kaydeder. Birkaç saniye sürer ve eğitimde başka hiçbir şey değişmez. Güncellemenin tamamı tek bir geri alma adımıdır.\n" +
            "- **Şimdi değil** pencereyi kapatır; eğitimi bir sonraki açışınızda pencere yeniden görünür.\n" +
            "- Güncelleme tamamlanamazsa bağlantınızı kontrol edip **Tekrar dene**'ye tıklayın.",
        },
      ],
    },
    es: {
      feature: "Línea de tiempo",
      title: "Línea de tiempo y animación",
      sections: [
        {
          id: "overview",
          title: "Qué es la línea de tiempo",
          videoUrl: null,
          text:
            "La línea de tiempo está acoplada bajo el lienzo y controla **cuándo** aparece cada elemento, cuánto tiempo permanece y cómo se mueve. Contráela o despliégala con el botón Timeline de su esquina superior izquierda, y arrastra su borde superior para hacerla más alta o más baja.\n\n" +
            "- Cada **fila** contiene clips. Los elementos del mismo tipo (imágenes, vídeos, textos, audio y narración) comparten una fila y se reproducen uno tras otro; las formas y los elementos interactivos e inteligentes tienen una fila propia.\n" +
            "- **De izquierda a derecha avanza el tiempo.** La barra de un clip muestra cuándo está ese elemento en pantalla: su inicio y su final.\n" +
            "- La línea vertical es el **cabezal**: el momento que se muestra en ese instante en el lienzo.\n" +
            "- Las filas siguen el orden de apilamiento: la fila superior es el elemento que está más al frente en el lienzo.\n\n" +
            "Haz clic en un clip para seleccionar su elemento en el lienzo. Si el elemento seleccionado no es visible en la posición del cabezal, el cabezal salta hasta donde está.\n\n" +
            "Si no ves la línea de tiempo, comprueba que **Línea de tiempo visible** esté activada en **Configuración Global**.",
        },
        {
          id: "play-scrub",
          title: "Reproducir y desplazarse",
          videoUrl: null,
          text:
            "Previsualiza la escena sin salir del editor.\n\n" +
            "- **Reproducir / Pausar** ejecuta la escena desde el cabezal. La reproducción se detiene al final de la escena; si vuelves a pulsar Reproducir, empieza de nuevo desde el principio.\n" +
            "- **Ir al inicio** (el botón de retroceso) devuelve el cabezal a 00:00.\n" +
            "- **Velocidad de vista previa** (junto a Reproducir): el botón del indicador pasa a la siguiente velocidad y la flecha abre una lista de 0.5× a 4×. Solo afecta a la vista previa del editor, no a la formación publicada; los vídeos de YouTube permiten como máximo 2×.\n" +
            "- Arrastra el cabezal, o haz clic y arrastra sobre la regla, para **desplazarte** a cualquier momento. El indicador muestra el tiempo actual sobre la duración de la escena, en minutos:segundos.centésimas.\n" +
            "- Cuando la reproducción llega a un elemento interactivo, se pausa y muestra una insignia **Interactivo**; pulsa **Saltar** para continuar sin completar la interacción.\n\n" +
            "Para ver los elementos que están fuera de su intervalo de tiempo mientras trabajas, usa el modo fantasma (consulta Zoom, modo fantasma, ocultar y bloquear todo).",
        },
        {
          id: "box-timing",
          title: "Tiempo y duración de los elementos",
          videoUrl: null,
          text:
            "Controla cuándo aparece un elemento y cuánto tiempo permanece.\n\n" +
            "- Arrastra el **borde izquierdo** de un clip para cambiar cuándo **aparece** el elemento; arrastra el **borde derecho** para cambiar cuándo **desaparece**. En los clips de vídeo y audio, los bordes recortan el medio en lugar de estirarlo.\n" +
            "- Arrastra un clip por el centro para moverlo sin cambiar su duración. Los bordes se ajustan al cabezal, al inicio y al final de la escena y a los bordes de otros clips; una línea guía muestra el ajuste.\n" +
            "- En un vídeo seleccionado que se reproduce automáticamente, la barra de reproducción interior define qué parte del vídeo se reproduce y cuándo empieza; arrastra la barra o sus bordes.\n" +
            "- Los elementos nuevos se colocan en la posición del cabezal, no al inicio de la escena, y se unen a una fila libre de su tipo.\n\n" +
            "Un elemento sin hora de fin permanece en pantalla hasta que termina la escena. La duración de la propia escena se define por separado: consulta Duración de la escena.",
        },
        {
          id: "animations",
          title: "Animaciones de entrada y salida",
          videoUrl: null,
          text:
            "Dale movimiento a un elemento al entrar o al salir.\n\n" +
            "- Selecciona el elemento y abre **Efecto** en el menú derecho (o haz clic derecho en un clip que no sea multimedia y elige el icono de animación). El panel tiene las pestañas **Entrada** y **Salida**.\n" +
            "- Efectos de entrada: **Entrada Pan**, **Aparecer**, **Acercar**, **Deslizar a la derecha**, **Entrar desde la izquierda**, **Deslizar arriba**, **Deslizar abajo**, **Girar**, **Rebotar**, **Entrar rebotando**, **Entrar girando**, **Voltear**, **Molinete**, **Rodar** y **Estirar**. Los elementos de texto también tienen **Máquina de escribir** y **Texto ondulado**.\n" +
            "- Efectos de salida: **Salida Pan**, **Desvanecer**, **Alejar**, **Salir a la derecha**, **Salir a la izquierda**, **Salir arriba**, **Salir abajo**, **Salir girando** y **Salir rebotando** (además de **Máquina de escribir (salida)** y **Texto ondulado (salida)** para el texto).\n" +
            "- La primera tarjeta, tachada, quita el efecto. El control deslizante define cuánto dura el efecto (hasta 3 segundos; en Pan, hasta la duración del clip).\n" +
            "- **Pan (Ken Burns)** es un zoom lento hacia un punto o alejándose de él. Define la **Dirección** (**Acercar** / **Alejar**), la **Cantidad de Zoom** y el **Punto de Inicio** en el selector o como porcentajes X/Y. Da a las escenas de capturas de pantalla y de vídeo esa sensación de “mostrar más de cerca”.\n\n" +
            "La entrada se ejecuta al inicio del clip y la salida al final. En la línea de tiempo, una franja sombreada marca cada una sobre el clip, y el botón **Efecto** muestra un pequeño punto cuando el elemento tiene alguna de las dos.",
        },
        {
          id: "narration",
          title: "Narración y Sincronizar con IA",
          videoUrl: null,
          text:
            "Sincroniza los elementos visuales con la locución.\n\n" +
            "- La narración y los sonidos de la escena aparecen como clips de audio con forma de onda, en la misma regla de tiempo que los elementos. Las filas de narración están debajo de las filas de elementos.\n" +
            "- Haz doble clic en un clip de narración para abrirlo en el diálogo de sonido.\n" +
            "- Haz clic derecho en un clip de narración y elige **Repetir en la escena** para reproducirlo en bucle durante toda la escena.\n" +
            "- **Sincronizar con IA**, en la barra de herramientas de la línea de tiempo, pide confirmación (**Iniciar sincronización**) y después ajusta los tiempos de las imágenes y los textos de la escena a su audio. Después puedes seguir ajustando cualquier clip a mano. La guía dedicada a Sincronizar con IA lo explica en detalle.",
        },
        {
          id: "zoom-bulk",
          title: "Zoom, modo fantasma, ocultar y bloquear todo",
          videoUrl: null,
          text:
            "Trabaja más rápido en escenas con muchos elementos. Estos controles están en la esquina superior izquierda de la línea de tiempo, encima de las etiquetas de las filas.\n\n" +
            "- **Alejar** (−) y **Acercar** (+) cambian la escala de tiempo; haz clic en el porcentaje para **Restablecer zoom (100%)**. Hasta que hagas zoom o hagas clic en las pistas, la línea de tiempo ajusta toda la escena a su ancho.\n" +
            "- El botón del cuadrado discontinuo activa o desactiva el modo fantasma. Con él activado y la reproducción detenida, los elementos que están fuera de su intervalo de tiempo se dibujan atenuados en su sitio en lugar de ocultarse, para que puedas colocar cosas que aún no están en pantalla.\n" +
            "- **Ocultar todo** / **Mostrar todo** y **Bloquear todo** / **Desbloquear todo** cambian todas las filas a la vez.\n" +
            "- Cada etiqueta de fila tiene también su propio ojo (**Ocultar** / **Mostrar**) y candado (**Bloquear** / **Desbloquear**). Los elementos ocultos también se ocultan para los alumnos; los elementos bloqueados no se pueden mover ni redimensionar en el lienzo ni en la línea de tiempo.",
        },
        {
          id: "shared-rows",
          title: "Filas compartidas y orden de apilamiento",
          videoUrl: null,
          text:
            "Los elementos del mismo tipo comparten una fila para que la línea de tiempo sea compacta.\n\n" +
            "- Una fila compartida muestra un icono de tipo con el número de miembros; su ojo y su candado actúan sobre todos los clips de la fila.\n" +
            "- Al soltar un clip en un lugar ocupado, los clips vecinos se desplazan en lugar de superponerse. Solo las filas del mismo tipo aceptan el clip; suéltalo entre dos filas para darle una fila propia.\n" +
            "- Arrastra el asa de la etiqueta de una fila hacia arriba o hacia abajo para cambiar el orden de apilamiento: una fila más alta queda delante en el lienzo. Las filas bloqueadas no se pueden mover.\n" +
            "- Un clip añadido mientras estás desplazado a otra zona se muestra automáticamente y parpadea brevemente.\n" +
            "- Los elementos inteligentes con partes animadas muestran una flecha en su clip; despliégala para obtener una subfila por cada parte, cada una con su propio tiempo.",
        },
        {
          id: "clip-actions",
          title: "Menú contextual del clip",
          videoUrl: null,
          text:
            "Haz clic derecho en un clip para acceder a acciones rápidas. Pasa el ratón sobre un icono para ver su nombre; las dos acciones de corte también explican lo que hacen o por qué no están disponibles.\n\n" +
            "- **Dividir en el cabezal** corta un clip de vídeo o audio en dos en la posición del cabezal. Las partes llevan una insignia de enlace (**Clip dividido — mismo origen**) y, al pasar el ratón sobre una, se resaltan las demás.\n" +
            "- **Cortar y eliminar** quita la parte del clip anterior al cabezal y desplaza el resto hasta el inicio del clip; al pasar el ratón por encima se previsualiza la parte que se eliminará.\n" +
            "- Ambos cortes requieren que el cabezal esté dentro del clip; si no, el menú indica **Coloca primero el cabezal dentro del clip**.\n" +
            "- **Bucle** repite un clip de vídeo; **Repetir en la escena** repite una narración durante toda la escena.\n" +
            "- **Alinear al elemento anterior** / **Alinear al elemento siguiente** acercan el clip hasta su vecino.\n" +
            "- **Eliminar** borra el elemento. Con un clip seleccionado, la tecla Delete hace lo mismo.\n" +
            "- En los clips de imagen, **Aidea Wizard AI** abre las herramientas de imagen con IA.",
        },
        {
          id: "scene-duration",
          title: "Duración de la escena",
          videoUrl: null,
          text:
            "La duración de la escena se define independientemente de sus clips.\n\n" +
            "- El indicador de la barra de herramientas muestra el tiempo actual sobre la duración de la escena. Haz clic en el total para escribir una nueva duración (minutos:segundos) y pulsa Enter.\n" +
            "- O arrastra el marcador de fin de escena en las pistas; no puede bajar de 5 segundos.\n" +
            "- El candado junto al total (y en el marcador) fija la duración de la escena para que no se cambie por accidente. Haz clic de nuevo para desbloquearla.\n\n" +
            "Cambiar la duración de la escena nunca cambia cuánto tiempo permanecen los elementos. Una escena nueva toma su duración de su contenido, y **Sincronizar con IA** la reajusta a la narración.",
        },
        {
          id: "media-assets",
          title: "Vista de Recursos Multimedia",
          videoUrl: null,
          text:
            "El botón de cuadrícula de la barra de herramientas de la línea de tiempo (**Mostrar recursos multimedia**) abre **Recursos Multimedia**, un resumen de los vídeos, imágenes y audios usados en la formación.\n\n" +
            "- Filtra por tipo desde la lista de categorías de la izquierda.\n" +
            "- **Solo esta escena** está activado por defecto; desactívalo para ver los medios de todas las escenas.\n" +
            "- Haz clic en un elemento para previsualizarlo, o reproduce un audio en el mismo lugar. Cuando se muestran todas las escenas, el menú de cada elemento ofrece **Vista previa** e **Ir a la escena**.",
        },
        {
          id: "legacy-update",
          title: "Actualizar formaciones antiguas",
          videoUrl: null,
          text:
            "Las formaciones creadas antes de la nueva línea de tiempo pueden tener vídeos y clips de audio sin una duración guardada en la línea de tiempo. Al abrir una formación así, aparece el cuadro de diálogo **Se necesita una actualización rápida**. Sin la actualización, las escenas pueden quedar cortas y los vídeos exportados pueden perder el sonido.\n\n" +
            "- El cuadro muestra cuántos vídeos y clips de audio necesitan la actualización y cuántas escenas se ven afectadas.\n" +
            "- **Actualizar ahora** comprueba la duración real de cada archivo multimedia (**Comprobando archivos multimedia…** con un contador), la escribe en la línea de tiempo y después guarda la formación. Tarda unos segundos y nada más cambia en la formación. Toda la actualización es un único paso de deshacer.\n" +
            "- **Ahora no** cierra el cuadro; volverá a aparecer la próxima vez que abras la formación.\n" +
            "- Si la actualización no puede completarse, comprueba tu conexión y haz clic en **Reintentar**.",
        },
      ],
    },
  },
};

export default timelineGuide;
